import { defineRouting } from "next-intl/routing"

/**
 * Platform-wide locale routing (US-009). `locales` is the **superset** of every
 * language any tenant may offer — next-intl's static `routing` cannot express a
 * per-tenant set, so the tenant's offered subset is gated at request time in
 * `app/[locale]/(store)/layout.tsx` (redirect off-locale → tenant default) and the
 * language switcher is driven from store config (US-014, Q2).
 */
export const routing = defineRouting({
  locales: ["en-GB", "fa", "tr"],
  defaultLocale: "en-GB",
  localePrefix: "always",
})

export type AppLocale = (typeof routing.locales)[number]

/**
 * Map a locale tag from OUTSIDE the platform (store config sent by the backend,
 * a stale bookmark, an `accept-language` value) onto a platform locale.
 *
 * The English locale is the regional tag `en-GB`, but the storefront service —
 * and any tenant configured before the rename — still reports the bare language
 * tag `en`. Matching falls back to the base language subtag, so `en`/`en-US`
 * resolve to `en-GB`. Returns null when nothing matches, letting the caller keep
 * its own fallback.
 */
export function normalizeLocale(
  tag: string | null | undefined
): AppLocale | null {
  if (!tag) return null
  const wanted = tag.toLowerCase()
  const exact = routing.locales.find((l) => l.toLowerCase() === wanted)
  if (exact) return exact
  const base = wanted.split("-")[0]
  return (
    routing.locales.find((l) => l.toLowerCase().split("-")[0] === base) ?? null
  )
}

/** LTR/RTL direction for the active locale. Persian (fa) is RTL. */
export function localeDirection(locale: string): "ltr" | "rtl" {
  return locale === "fa" ? "rtl" : "ltr"
}
